import type { ToolPageEntry, ToolPageFaq, ToolPageRelated } from './toolPages';
import { GRADING_SCALE_SIZES, aOrAn, gradingScalePath, gradingScaleSlug } from './gradingScaleSizes';
import { SEO_KEYWORDS_BY_PATH } from './seoKeywordMap';
import {
  SCALE_PLUS_MINUS,
  SCALE_STANDARD,
  bandRanges,
  computeStats,
  percentToLetter,
  roundTo,
} from '../utils/academicMath';
import { buildQuickChart } from '../utils/gradeCalculations';

/**
 * Generated, per-size landing pages for /grading-scale/N-questions/, the grading-scale index and the
 * Average Grade calculator. Every number printed on these pages comes from the same math modules the
 * calculators use, so the text can never disagree with the tool. Each size gets different computed
 * numbers, different "how many can I miss" thresholds and size-specific guidance, so the pages are
 * not templated copies of each other.
 * Strengthened 2026-10-28 for the pages sitting at search positions 8-10: search-phrased titles,
 * printable meta descriptions, grade-curve and letter-grade related links, a minimum-correct
 * section, and a pass-mark FAQ on every size except the two (60 and 20 questions) where the
 * 2026-10-26 keyword round already adds a passing-score FAQ, all computed per size.
 */

const fmt = (x: number, d = 2): string => String(Number(x.toFixed(d)));

function chartFor(n: number) {
  return buildQuickChart({ total: n, decimals: 1, bands: SCALE_STANDARD });
}

/** The most wrong answers allowed while still earning each standard letter (null when the letter is out of reach). */
function maxWrongByLetter(n: number): Record<string, number | null> {
  const rows = chartFor(n);
  const out: Record<string, number | null> = {};
  for (const letter of ['A', 'B', 'C', 'D']) {
    const hits = rows.filter((r) => r.letter === letter);
    out[letter] = hits.length ? Math.max(...hits.map((r) => r.wrong)) : null;
  }
  return out;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const article = (letter: string) => (letter === 'A' ? 'an' : 'a');
const missPhrase = (w: number | null, letter: string) =>
  w === null
    ? `${article(letter)} ${letter} is not reachable with whole questions`
    : `${w === 0 ? 'no questions' : `up to ${plural(w, 'question', 'questions')}`} for ${article(letter)} ${letter}`;

function sizeContext(n: number, p: number): string {
  if (n <= 10) {
    return `On a short ${n}-question quiz every single answer matters: one missed question moves the score by ${fmt(p)} percentage points, which is often enough to change the letter grade.`;
  }
  if (n <= 25) {
    return `${aOrAn(n, true)} ${n}-question test is a common size for unit quizzes and chapter tests. One missed question costs ${fmt(p)} points, so a single slip rarely decides the letter on its own but two or three often do.`;
  }
  if (n <= 50) {
    return `With ${n} questions each answer is worth only ${fmt(p)}%, so the grade is steadier than on a short quiz. This size is typical for midterms and multi-section exams.`;
  }
  return `${aOrAn(n, true)} ${n}-question exam spreads the risk thinly: each answer is worth just ${fmt(p)}%, so you can miss several questions before the letter grade moves.`;
}

function pickWrongs(n: number): number[] {
  const raw = [0, 1, 2, 3, Math.ceil(n * 0.1), Math.ceil(n * 0.2), Math.ceil(n * 0.3), Math.ceil(n * 0.4)];
  return Array.from(new Set(raw.filter((w) => w >= 0 && w <= n))).sort((a, b) => a - b);
}

export function buildQuestionCountPage(n: number): ToolPageEntry {
  const p = 100 / n;
  const rows = chartFor(n);
  const rowFor = (wrong: number) => rows.find((r) => r.wrong === wrong)!;
  const plusLetter = (percent: number) => percentToLetter(roundTo(percent, 1), SCALE_PLUS_MINUS).letter;
  const maxWrong = maxWrongByLetter(n);
  const exampleWrong = Math.max(1, Math.round(n * 0.12));
  const faqWrong = Math.max(2, Math.ceil(n * 0.15));
  const ex = rowFor(exampleWrong);
  const fq = rowFor(faqWrong);
  const wrongs = pickWrongs(n);
  const half = n <= 200;

  const allowParts: string[] = [];
  for (const letter of ['A', 'B', 'C', 'D']) {
    const w = maxWrong[letter];
    allowParts.push(missPhrase(w, letter));
  }

  // Lowest correct count that still earns each letter. Whole-question scores move in
  // steps, so each letter starts at a count, not an exact percentage. Computed from the
  // same chart rows, so the numbers can never disagree with the tool.
  const minCorrectByLetter = (['A', 'B', 'C', 'D'] as const).map((letter) => {
    const w = maxWrong[letter];
    return { letter, correct: w === null ? null : n - w, pct: w === null ? null : rowFor(w).formattedPercentage };
  });
  const passW = maxWrong['D'];
  const passCorrect = passW === null ? null : n - passW;
  const passPct = passW === null ? null : rowFor(passW).formattedPercentage;

  const idx = GRADING_SCALE_SIZES.indexOf(n);
  const prev = idx > 0 ? GRADING_SCALE_SIZES[idx - 1] : null;
  const next = idx < GRADING_SCALE_SIZES.length - 1 ? GRADING_SCALE_SIZES[idx + 1] : null;
  const related: ToolPageRelated[] = [];
  if (prev) related.push({ path: gradingScalePath(prev), label: `${prev}-question grading scale`, blurb: `Score chart for ${aOrAn(prev)} ${prev}-question test.` });
  if (next) related.push({ path: gradingScalePath(next), label: `${next}-question grading scale`, blurb: `Score chart for ${aOrAn(next)} ${next}-question test.` });
  related.push(
    { path: '/grading-scale/', label: 'All grading scale charts', blurb: 'Pick any test size from 5 to 100 questions.' },
    { path: '/', label: 'Quick Grade calculator', blurb: 'Grade a whole class and print a chart for any size.' },
    { path: '/test-grade-calculator/', label: 'Test grade calculator', blurb: 'Points earned out of points possible, with bonus points.' },
    { path: '/grade-curve-calculator/', label: 'Grade curve calculator', blurb: `See how a curve would shift every score on ${aOrAn(n)} ${n}-question test.` },
    { path: '/letter-grade-calculator/', label: 'Letter grade calculator', blurb: `Turn any ${n}-question score into a letter grade and GPA points.` }
  );

  const faqs: ToolPageFaq[] = [
    {
      question: `How much is each question worth on ${aOrAn(n)} ${n}-question test?`,
      answer: `Each question is worth 100 ÷ ${n} = ${fmt(p, 3)}% when every question has the same weight. Missing one question leaves ${fmt(100 - p, 2)}%, and each further miss lowers the score by another ${fmt(p, 3)} points.`,
    },
    {
      question: `How many questions can I get wrong on ${aOrAn(n)} ${n}-question test and still get an A?`,
      answer: `On ${aOrAn(n)} ${n}-question test with the common 90/80/70/60 scale you can miss ${allowParts[0]}. For the other letters you can miss ${allowParts.slice(1).join(', ')}. Your teacher’s scale may use different cutoffs, so check the syllabus.`,
    },
    {
      question: `What grade is ${faqWrong} wrong out of ${n}?`,
      answer: `${plural(faqWrong, 'wrong answer', 'wrong answers')} leaves ${n - faqWrong} correct, which is ${fq.formattedPercentage}%. That is a ${fq.letter} on the standard A–F scale and a ${plusLetter(fq.rawPercentage)} on the plus/minus scale.`,
    },
    {
      question: `Can I give partial credit on ${aOrAn(n)} ${n}-question test?`,
      answer: half
        ? `Yes. On ${aOrAn(n)} ${n}-question test, turn on half points in the Quick Grade calculator and a half-credit answer counts as ${fmt(p / 2, 3)}% instead of ${fmt(p, 3)}%. The chart on this page uses whole questions only.`
        : `For partial credit on ${aOrAn(n)} ${n}-question test, use a points-based calculation: add the points earned on every question, divide by the points possible and multiply by 100.`,
    },
  ];

  // The 2026-10-26 keyword round (seoExtras, applied later in toolPages.ts) already
  // adds a passing-score FAQ on the sizes whose keyword map contains one (60 and 20
  // questions). A second pass FAQ there would duplicate it, so this one is added only
  // on the other sizes.
  const hasPassKeyword = (SEO_KEYWORDS_BY_PATH[gradingScalePath(n)] ?? []).some((k) => /passing score/.test(k));
  if (!hasPassKeyword && passCorrect !== null && passPct !== null) {
    faqs.push({
      question: `What score do I need to pass ${aOrAn(n)} ${n}-question test?`,
      answer: `On the common 90/80/70/60 scale, passing starts at a D, which needs 60% or more. On ${aOrAn(n)} ${n}-question test that means at least ${passCorrect} correct (${passPct}%). Anything below that is an F. Some teachers set the pass line higher, so check the syllabus.`,
    });
  }

  return {
    slug: gradingScaleSlug(n),
    path: gradingScalePath(n),
    navLabel: `${n} Questions`,
    h1: `${n}-Question Grading Scale`,
    title: `Grading Scale for ${n} Questions: Percent & Letter Chart`,
    metaDescription: `${n}-question grading scale: each answer is worth ${fmt(p, 2)}%. Percentage and letter grade for every score, e.g. ${exampleWrong} wrong = ${ex.formattedPercentage}%. Printable, free.`,
    badge: `${n}-question test`,
    intro: `Each question on ${aOrAn(n)} ${n}-question test is worth ${fmt(p, 2)}%. On the common 90/80/70/60 scale you can miss ${allowParts.join(', ')}. The chart below shows the percentage and letter grade for every number of wrong answers from 0 to ${n}.`,
    questions: n,
    keywords: [
      `${n} question grading scale`,
      `${n} question test grade calculator`,
      `grading scale for ${n} questions`,
      `${n} question quiz score chart`,
    ],
    featureList: [
      `Score chart for 0 to ${n} wrong answers`,
      'Standard A–F and plus/minus letter grades',
      'Printable chart with no sign-up',
    ],
    sections: [
      {
        heading: `How ${aOrAn(n)} ${n}-question test is scored`,
        paragraphs: [
          `Divide the number of correct answers by ${n} and multiply by 100. With ${n} questions worth the same amount, each one is ${fmt(p, 3)}% of the grade. ${sizeContext(n, p)}`,
          `On the common 90/80/70/60 scale you can miss ${allowParts.join(', ')}. Anything below the D line is an F.`,
        ],
      },
      {
        heading: `Quick reference for ${n} questions`,
        paragraphs: [`These scores come from the same chart you see in the tool above.`],
        bullets: wrongs.map((w) => {
          const r = rowFor(w);
          return `${plural(w, 'wrong answer', 'wrong answers')} (${n - w} correct) = ${r.formattedPercentage}%, grade ${r.letter}`;
        }),
      },
      {
        heading: `Minimum correct answers on ${aOrAn(n)} ${n}-question test`,
        paragraphs: [
          `Because only whole-question scores are possible, each letter starts at a specific count rather than an exact percentage. On ${aOrAn(n)} ${n}-question test the lowest score that still earns each letter is:`,
        ],
        bullets: minCorrectByLetter.map(({ letter, correct, pct }) =>
          correct === null
            ? `${letter}: not reachable with whole questions`
            : `${letter}: at least ${correct} correct (${pct}%)`
        ),
      },
      {
        heading: `Rounding and partial credit on ${n} questions`,
        paragraphs: [
          Number.isInteger(p)
            ? `Because ${n} divides 100 evenly, every score is a whole percentage: no rounding is needed.`
            : `Because ${n} does not divide 100 evenly, scores are rounded. For example, ${exampleWrong} wrong is ${fmt(ex.rawPercentage, 4)}% exactly and shows as ${ex.formattedPercentage}% rounded to one decimal. The letter is chosen from the rounded value that is displayed.`,
          half
            ? `Half points are possible too: one half-credit answer is worth ${fmt(p / 2, 3)}%.`
            : 'For partial credit, use a points-based calculation instead of counting whole questions.',
        ],
      },
    ],
    example: {
      heading: `Worked example: ${exampleWrong} wrong on ${aOrAn(n)} ${n}-question test`,
      scenario: `A student misses ${exampleWrong} of ${n} questions.`,
      rows: [
        { label: 'Correct answers', value: `${n - exampleWrong} of ${n}` },
        { label: 'Percentage', value: `${ex.formattedPercentage}%` },
        { label: 'Standard A–F', value: ex.letter },
        { label: 'Plus/minus', value: plusLetter(ex.rawPercentage) },
      ],
      takeaway: `(${n} − ${exampleWrong}) ÷ ${n} × 100 = ${fmt(ex.rawPercentage, 2)}%, so the grade is ${ex.formattedPercentage}% (${ex.letter}).`,
    },
    faqs,
    related,
  };
}

/* ---------- index page ---------- */

export function buildGradingScaleIndexPage(): ToolPageEntry {
  const sample = [10, 20, 25, 50, 100].filter((n) => GRADING_SCALE_SIZES.includes(n));
  return {
    slug: 'grading-scale',
    path: '/grading-scale/',
    navLabel: 'Grading Scales',
    h1: 'Grading Scale Generator',
    title: 'Grading Scale Generator — Charts for 5 to 100 Questions',
    metaDescription: `Free grading scale generator: build a printable chart for any test, from ${GRADING_SCALE_SIZES[0]} to ${GRADING_SCALE_SIZES[GRADING_SCALE_SIZES.length - 1]} questions, with the percent and letter grade for every score.`,
    badge: 'Score charts',
    intro: 'Choose the number of questions on your test to open a ready-made score chart. Each chart lists the percentage and letter grade for every number of wrong answers, on the standard and plus/minus scales.',
    keywords: [
      'grading scale chart',
      'grading scale generator',
      'test grade scale generator',
      'grading scale by questions',
      'printable grading chart',
      'teacher grading chart printable',
      'printable grade scale chart',
      'printable grader chart',
      'test score chart',
      'quiz grading scale',
    ],
    featureList: ['Charts for 17 common test sizes', 'Standard and plus/minus letter grades', 'Printable, free, no sign-up'],
    lookupTables: [
      {
        heading: 'A–F grading scale (90/80/70/60)',
        intro: 'The common US scale without plus or minus. Your school may use different cutoffs.',
        columns: ['Letter', 'Percent range', 'GPA points'],
        rows: bandRanges(SCALE_STANDARD)
          .filter((b) => b.letter !== 'F')
          .map((b) => [b.letter, `${b.from}–${b.to}%`, b.gpa.toFixed(1)])
          .concat([['F', 'Below 60%', '0.0']]),
      },
      {
        heading: 'Plus/minus grading scale',
        intro: 'The common US plus/minus scale. GPA points are the usual 4.0-scale values.',
        columns: ['Letter', 'Percent range', 'GPA points'],
        rows: bandRanges(SCALE_PLUS_MINUS)
          .filter((b) => b.letter !== 'F')
          .map((b) => [b.letter, `${b.from}–${b.to}%`, b.gpa.toFixed(1)])
          .concat([['F', 'Below 60%', '0.0']]),
      },
    ],
    sections: [
      {
        heading: 'How these charts work',
        paragraphs: [
          'Every chart uses the same rule: percentage = correct answers ÷ total questions × 100, then the letter grade is read from the scale. The standard scale is A 90, B 80, C 70, D 60. The plus/minus scale splits each letter into thirds.',
          'Short quizzes swing the most: on a 10-question quiz one wrong answer is 10 points, while on a 100-question exam it is 1 point.',
          'That is why the grading scale by questions is a separate chart for each test size: the same two misses mean something different on a 10-question quiz than on a 50-question test.',
        ],
      },
      {
        heading: 'Printable grading chart for any test',
        paragraphs: [
          'A printable grading chart saves you from dividing on every paper. Pick the number of questions on your test, open the chart and print it: you get a one-page printable grade scale chart that lists the percentage and letter grade for every number of wrong answers. Teachers who search for a teacher grading chart printable usually want exactly this, a sheet they can keep beside the stack of tests.',
          `The same page works as a printable grader chart for quizzes, unit tests and finals. Charts are ready for ${GRADING_SCALE_SIZES.length} common test sizes, and the Quick Grade calculator on the home page builds one for any whole number of questions from 1 to 1000.`,
        ],
      },
      {
        heading: 'How do I print a grading chart for my class?',
        paragraphs: ['Printing takes four steps:'],
        bullets: [
          'Open the chart for your test size from the list above.',
          'Check that the scale matches your syllabus, or set your own cutoffs in the Quick Grade calculator first.',
          'Use the print button. The printed page contains only the chart, without the site navigation.',
          'Keep the sheet beside the tests, or save the printout as a PDF for your class folder.',
        ],
      },
      {
        heading: 'Printable grading chart vs grading by hand',
        paragraphs: [
          'Grading by hand means subtracting the wrong answers, dividing by the number of questions, multiplying by 100 and looking up the letter, again for every paper. A printable grading chart does that arithmetic once, so every student with the same score gets the same percentage and letter, and you only count wrong answers. Hand grading still fits essays and rubric work, where the score comes from your judgment rather than a count.',
        ],
      },
      {
        heading: 'What is the easiest printable grading tool for teachers?',
        paragraphs: [
          'The easiest printable grading tool is a chart built for your exact test size: no formulas, no account and a layout that fits one page. The charts here do that for common test sizes, and the Quick Grade calculator covers every other size with your own letter cutoffs.',
        ],
      },
      {
        heading: 'What one wrong answer costs',
        paragraphs: ['Points lost per missed question for common test sizes:'],
        bullets: sample.map((n) => `${n} questions: ${fmt(100 / n, 2)}% per question`),
      },
    ],
    example: {
      heading: 'Worked example: the same two misses on different tests',
      scenario: 'Two wrong answers cost very different amounts depending on the test size.',
      rows: sample.map((n) => {
        const r = chartFor(n).find((x) => x.wrong === 2)!;
        return { label: `2 wrong out of ${n}`, value: `${r.formattedPercentage}% (${r.letter})` };
      }),
      takeaway: 'The more questions a test has, the less any single miss matters.',
    },
    faqs: [
      {
        question: 'Which grading scale do these charts use?',
        answer: 'The standard chart uses A 90, B 80, C 70, D 60. The plus/minus chart uses A+ 97, A 93, A- 90, B+ 87, B 83, B- 80, C+ 77, C 73, C- 70, D+ 67, D 63, D- 60. If your teacher uses different cutoffs, set them in the Quick Grade calculator.',
      },
      {
        question: 'My test has a different number of questions. What now?',
        answer: 'Use the Quick Grade calculator on the home page. It builds the same chart for any whole number of questions from 1 to 1000.',
      },
      {
        question: 'Do the charts round the percentage?',
        answer: 'Yes, to one decimal place. The letter grade is chosen from that rounded percentage, so the two always agree.',
      },
      {
        question: 'Can I print a chart?',
        answer: 'Yes. Open a chart and use the print button, or open the Quick Grade calculator for a full printable chart with your own cutoffs.',
      },
      {
        question: 'How do I print a grading chart for my class?',
        answer: 'Open the chart for your test size, check that the scale matches your syllabus and use the print button. The printed page contains only the chart. If you need custom cutoffs, set them in the Quick Grade calculator first and print from there.',
      },
      {
        question: 'What is the easiest printable grading tool for teachers?',
        answer: 'A chart made for your test size is the easiest: you count the wrong answers on a paper and read the percentage and letter straight across. Start with the chart for your number of questions, or use the Quick Grade calculator for any other size.',
      },
      {
        question: 'Is a printable grading chart faster than grading by hand?',
        answer: 'For tests with right and wrong answers, yes. Looking up a row takes seconds, while repeating the division and the letter conversion on every paper takes longer and invites rounding errors. Grade essays and rubric-based work by hand.',
      },
      {
        question: 'Is there a printable grade scale chart for every test size?',
        answer: 'There are ready-made charts for the most common test sizes, and the Quick Grade calculator builds the same chart for any whole number of questions from 1 to 1000, so a printable grade scale chart is always available for your test.',
      },
    ],
    related: [
      { path: '/', label: 'Quick Grade calculator', blurb: 'Any test size, custom cutoffs, class tally.' },
      { path: '/test-grade-calculator/', label: 'Test grade calculator', blurb: 'Points earned out of points possible.' },
      { path: '/letter-grade-calculator/', label: 'Letter grade calculator', blurb: 'Percent to letter and GPA points.' },
      { path: '/average-grade-calculator/', label: 'Average grade calculator', blurb: 'Mean, median and letter for a list of scores.' },
    ],
  };
}

/* ---------- average grade calculator ---------- */

const avgScores = [88, 92, 79, 95, 84];
const avgStats = computeStats(avgScores);
const avgLetter = percentToLetter(roundTo(avgStats.mean, 1), SCALE_STANDARD).letter;
const avgPlus = percentToLetter(roundTo(avgStats.mean, 1), SCALE_PLUS_MINUS).letter;

export function buildAverageGradePage(): ToolPageEntry {
  return {
    slug: 'average-grade-calculator',
    path: '/average-grade-calculator/',
    navLabel: 'Average Grade',
    h1: 'Average Grade Calculator',
    title: 'Average Grade Calculator: Mean, Median & Letter Grade',
    metaDescription: 'Paste your scores to get the average grade, the median, the highest and lowest score, and the letter grade. Works for tests, quizzes and assignments.',
    badge: 'Mean and median',
    intro: 'Enter your scores separated by commas, spaces or new lines. You get the average percentage, the median, the highest and lowest score and the letter grade on your chosen scale.',
    keywords: ['average grade calculator', 'calculate average grade', 'mean of test scores', 'class average calculator'],
    featureList: ['Mean, median, highest and lowest score', 'Letter grade on standard or plus/minus scale', 'Flags values it cannot read instead of ignoring them'],
    sections: [
      {
        heading: 'How to calculate an average grade',
        paragraphs: [
          'Add up all the scores and divide by how many there are: average = sum of scores ÷ number of scores. This is the arithmetic mean, and it treats every score as equally important.',
          'If your categories have different weights, such as exams worth more than homework, a plain average is not your course grade. Use the weighted grade calculator for that.',
        ],
      },
      {
        heading: 'Mean, median and why both matter',
        paragraphs: [
          'The median is the middle score when the list is sorted. One very low or very high score pulls the mean but barely moves the median, so comparing them shows whether a single outlier is driving your average.',
          'Scores out of different totals should be converted to percentages first. A 9 out of 10 and a 45 out of 50 are both 90%, but averaging 9 and 45 gives a meaningless 27.',
        ],
      },
      {
        heading: 'Averaging scores with different totals',
        paragraphs: [
          'Tests are rarely all marked out of the same number. Convert each result to a percentage, then average the percentages: a 9 out of 10, a 45 out of 50 and an 18 out of 25 become 90%, 90% and 72%, which average 84%.',
          'Adding the raw points instead gives 72 out of 85, or 84.7%. That figure leans toward the longer assessments, so it fits a course where every point counts the same. Your syllabus tells you which method your instructor uses.',
        ],
      },
      {
        heading: 'When the median tells a different story',
        paragraphs: [
          'Take five quiz results of 90, 92, 88, 91 and 30. The mean is 78.2%, yet four of the five sit around 90%. The median is 90%, which describes a typical result far better. When the two numbers drift apart, one unusual score is doing the damage, and it is worth asking whether your instructor drops it.',
          'If the mean and median land within a point or two of each other, the scores are evenly spread and either number is a fair summary.',
        ],
      },
      {
        heading: 'Dropping the lowest score',
        paragraphs: [
          'Remove the lowest value and average what is left. Dropping the 79 from 88, 92, 79, 95 and 84 leaves 359 across four scores, so the average rises from 87.6% to 89.75%. Only do this where the syllabus allows it; the calculator cannot know your instructor’s policy.',
        ],
      },
      {
        heading: 'What score lifts your average to a target?',
        paragraphs: [
          'Multiply the target by the number of scores you will have, then subtract the total you already hold. From 438 points across five tests, an 88% average after six tests needs 88 × 6 − 438 = 90 on the sixth. A 90% average would need 102, which is out of reach without extra credit, so you would have to plan across several tests.',
        ],
      },
      {
        heading: 'Average and median in Excel or Google Sheets',
        paragraphs: [
          'Put your percentages in one column, say A1:A5, then use =AVERAGE(A1:A5) for the mean and =MEDIAN(A1:A5) for the middle value. To drop the lowest score, use =(SUM(A1:A5)-MIN(A1:A5))/(COUNT(A1:A5)-1). Paste the same numbers here when you want the letter and a quick cross-check.',
        ],
      },
    ],
    example: {
      heading: 'Worked example: five test scores',
      scenario: `A student scores ${avgScores.join(', ')} on five tests.`,
      rows: [
        { label: 'Sum', value: String(avgScores.reduce((a, b) => a + b, 0)) },
        { label: 'Average', value: `${fmt(avgStats.mean, 1)}%` },
        { label: 'Median', value: `${fmt(avgStats.median, 1)}%` },
        { label: 'Letter (standard / plus-minus)', value: `${avgLetter} / ${avgPlus}` },
      ],
      takeaway: `${avgScores.reduce((a, b) => a + b, 0)} ÷ ${avgScores.length} = ${fmt(avgStats.mean, 1)}%, which is a ${avgLetter} on the standard scale.`,
    },
    faqs: [
      {
        question: 'How do I calculate my average grade?',
        answer: 'Add all your scores and divide by the number of scores. If every score is out of 100 this is your average percentage. If scores have different totals, convert each one to a percentage first.',
      },
      {
        question: 'Is the average grade the same as my course grade?',
        answer: 'Only when every assignment counts equally. If your syllabus gives different categories different weights, use the weighted grade calculator instead.',
      },
      {
        question: 'What is the difference between the mean and the median?',
        answer: 'The mean adds everything and divides by the count. The median is the middle value of the sorted list. They differ when a few scores are much higher or lower than the rest.',
      },
      {
        question: 'Why did the calculator leave out one of my values?',
        answer: 'It only accepts plain numbers. Text, negative numbers and values above the maximum score are listed under the input with the reason, so you can fix them.',
      },
      {
        question: 'Should I average percentages or add up all the points?',
        answer: 'Averaging percentages gives every assessment equal weight, while adding raw points lets longer assessments count for more. Results of 9/10, 45/50 and 18/25 average 84% by percentage but come to 84.7% by pooled points. Follow the method your syllabus names.',
      },
      {
        question: 'How do I drop the lowest score from an average?',
        answer: 'Take it out of the list and average the rest. Scores of 88, 92, 79, 95 and 84 average 87.6%; without the 79 they average 89.75%. Only drop a score when your instructor’s policy allows it.',
      },
      {
        question: 'What score do I need on the next test to reach a target average?',
        answer: 'Multiply the target by the number of scores including the next one, then subtract the sum so far. With a total of 438 over five tests, an 88% average needs 90 on the sixth.',
      },
    ],
    related: [
      { path: '/grade-calculator/', label: 'Weighted grade calculator', blurb: 'Categories with different weights.' },
      { path: '/test-grade-calculator/', label: 'Test grade calculator', blurb: 'Points earned out of points possible.' },
      { path: '/grade-curve-calculator/', label: 'Grade curve calculator', blurb: 'See how a curve changes the class average.' },
      { path: '/grading-scale/', label: 'Grading scale charts', blurb: 'Score charts for 5 to 100 questions.' },
    ],
  };
}
